import angular from 'angular';
import addChangeItemDialogTemplate from './AddChangeItemDialog.template.html';
import './AddChangeItemDialog.css';

/*******************************************************************************
 * AddChangeItemDialogProvider */
/**
 * Service: AddChangeItemDialog
 *
 * Provides a single method to display a dialog for the adding/updating of an
 * Item or ProductItem object.
 *******************************************************************************/
/* @ngInject */ export function AddChangeItemDialogProvider() {
  this.controller = controller;
  this.$get = [
    '$injector',
    function ($injector) {
      return {
        controller: this.controller,
        show: $injector.invoke(show, null, { $ctrl: this })
      };
    }
  ];
}

/*******************************************************************************
 * AddChangeItemDialogContentDirective */
/**
 * Directive: AddChangeItemDialogContent. Bound from the component usage:
 * hideIncidental, hideOptionQtyChange, item, resolve (evaluated with $item after
 * the item is saved) and cancel (evaluated when the cancel button is clicked).
 *******************************************************************************/
/* @ngInject */ export function AddChangeItemDialogContentDirective(
  AddChangeItemDialog
) {
  return {
    template: addChangeItemDialogTemplate,
    controller: AddChangeItemDialog.controller,
    controllerAs: '$ctrl',
    bindToController: true,
    scope: {
      cancel: '&',
      hideIncidental: '<',
      hideOptionQtyChange: '<',
      item: '<',
      modalInfo: '<',
      resolve: '&', //Will be provided bound item as $item
      tagLists: '<'
    }
  };
}

/*******************************************************************************
 * show */
/**
 * This will display a modal dialog to handle creating a new item or modifying an
 * existing one.
 *
 * @param  itemID  ID of item to modify. NaN = add new item.
 * @param  itemType  Item type that will be added or modified.
 * @param  anonDefaultValue  Allow Anonymous Sale. Defaults to false if no value.
 * @param  hideIncidental  Hides the incidental checkbox on the Sales Info tab.
 * @param  hideOptionQtyChange  Hides the qty change column and input.
 *******************************************************************************/
/* @ngInject */ function show(
  BusyScreen,
  $ctrl,
  $uibModal,
  $q,
  ItemAdminService,
  BooksData,
  InventoryProduct
) {
  /**
   * This covers for this modal being used in the equipment tracking app. The equipment tracking
   * application does not call the init function so the information for items, item categories, and
   * item options did not exist when opening this modal within Equipment Tracker. The solution is
   * to force this modal to initialize the service file so the service fills all those values that
   * the equipment tracker relies upon. We had to fix this because this made it so users could not
   * create a TextBook item within Equipment Tracker due to there being no item categories.
   *
   * Since item admin does call this I was worried we would see the getItemAdminInfo request fired twice
   * however it does not. Due to there being an initialized flag in the service that checks to make sure
   * it does not get initialized multiple times.
   * **/
  ItemAdminService.init();

  return function (
    itemID,
    itemType,
    anonDefaultValue,
    hideIncidental,
    hideOptionQtyChange
  ) {
    /*---------------------*/
    /* New item definition */
    /*---------------------*/
    var DEF_NEW_ITEM = {
      active: true,
      id: 0,
      fixedPrice: true,
      allowAnonymousSale: true,
      inventoryProducts: [],
      itemDiscounts: [],
      itemOptions: [],
      limiter: { balance: 0, grade: '', quantity: 0 },
      salesAcctList: [],
      subItems: [],
      statusTags: []
    };

    /*------------------*/
    /* Show the dialog. */
    /*------------------*/
    BusyScreen.setBusy(true);

    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      size: 'lg',
      template:
        '<add-change-item-dialog-content item="item" modal-info="modalInfo" tag-lists="tagLists" hide-incidental="hideIncidental" hide-option-qty-change="hideQty" resolve="$close($item)" cancel="$dismiss()"></add-change-item-dialog-content>',
      resolve: {
        data: function () {
          return ItemAdminService.getItemAdminDialogInfo(itemType, itemID);
        },
        hideIncidental: function () {
          return hideIncidental;
        },
        hideOptionQtyChange: function () {
          return hideOptionQtyChange;
        }
      },
      controller: function ($scope, data, hideIncidental, hideOptionQtyChange) {
        /***
         * This modalInfo object was added so we could consolidate all the requests that we needed to do into one. This decreased the load time substantially, since the server does not have to deal with multiple requests in parallel.
         * This modalInfo object was needed because we need to pass all the information we got into the add-change-item-dialog-content component. Before we would do 1 request at beginning here and then the other requests were done onInit of
         * component. Now we do it all initially so we need to pass all the info into the component.
         * **/
        $scope.modalInfo = {
          accounts: (data.accounts || []).sort(function (a, b) {
            return a.accountName
              .toLowerCase()
              .localeCompare(b.accountName.toLowerCase());
          }),
          vendorList: data.vendors || [],
          classList: (data.classes || []).sort(function (a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          }),
          revtrak: data.revtrak
        };

        $scope.tagLists = data.tagLists || {};
        $scope.hideIncidental = hideIncidental;
        $scope.hideQty = hideOptionQtyChange;

        var item = data.item
          ? data.item
          : Object.assign({}, DEF_NEW_ITEM, {
              type: Number(itemType),
              allowAnonymousSale: anonDefaultValue
            });

        if (item.saleEndDate) item.saleEndDate = new Date(item.saleEndDate);

        if (item.saleStartDate)
          item.saleStartDate = new Date(item.saleStartDate);

        if (item.inventoryProducts)
          item.inventoryProducts = item.inventoryProducts.map(function (ip) {
            return new InventoryProduct(ip);
          });
        /*---------------------------------------------------------*/
        /* TODO: The appropriate way to do this is to create a     */
        /* TODO: `BooksData.Item` and `BooksData.ProductItem` type */
        /* TODO: and create the appropriate one for the item type. */
        /*---------------------------------------------------------*/
        if (item.salesAcctList)
          item.salesAcctList = item.salesAcctList.map(function (item) {
            return new BooksData.ItemSalesAcct(item);
          });

        $scope.item = item;
      }
    }).result;
  };
}

/*******************************************************************************
 * controller */
/**
 * Controller for the add/change item dialog content.
 *******************************************************************************/
/* @ngInject */ function controller(
  $q,
  $scope,
  $templateCache,
  $timeout,
  AccountType,
  Alert,
  BooksSettings,
  BooksSvc,
  BooksData,
  BooksTools,
  BusyScreen,
  InventoryProduct,
  ItemAdminService,
  ManageDistributionsDialog,
  SERVER,
  Tools,
  AddChangeItemConfig
) {
  /*****************************************************************************
   * Constants
   *****************************************************************************/
  /*---------------*/
  /* Account Lists */
  /*---------------*/
  /** Restricted Account Types. */
  var RESTRICTED_ACCT_TYPES = [
    AccountType.BANK,
    AccountType.CASH,
    AccountType.UNDEPOSITED_FUNDS,
    AccountType.UNDEPOSITED_PAYPAL
  ];

  /** Restricted Adjustment Account Types. */
  var RESTRICTED_ADJ_ACCT_TYPES = RESTRICTED_ACCT_TYPES.concat([
    AccountType.INVENTORY
  ]);

  /** Restricted COGS Account Types. */
  var RESTRICTED_COGS_ACCT_TYPES = RESTRICTED_ACCT_TYPES.concat([
    AccountType.REVENUE,
    AccountType.NON_UNIT_REVENUE
  ]);
  /*----------------------*/
  /* Item Type Flags List */
  /*----------------------*/
  var TYPES = [
    {
      type: SERVER.BooksDBConstants.IT_DONATION,
      account: true,
      amount: false,
      baseItem: false,
      pos: true,
      subItems: false,
      deposit: true,
      forSchoolPay: false,
      forRemote: true
    },
    {
      type: SERVER.BooksDBConstants.IT_EQUIPMENT,
      account: false,
      amount: true,
      baseItem: true,
      pos: false,
      subItems: false,
      deposit: false,
      forSchoolPay: false,
      forRemote: false
    },
    {
      type: SERVER.BooksDBConstants.IT_FEES,
      account: true,
      amount: true,
      baseItem: false,
      pos: true,
      subItems: true,
      deposit: true,
      forSchoolPay: true,
      forRemote: true
    },
    {
      type: SERVER.BooksDBConstants.IT_FINES,
      account: true,
      amount: true,
      baseItem: false,
      pos: false,
      subItems: false,
      deposit: true,
      forSchoolPay: false,
      forRemote: true
    },
    {
      type: SERVER.BooksDBConstants.IT_GROUP,
      account: false,
      amount: false,
      baseItem: false,
      pos: true,
      subItems: false,
      deposit: true,
      forSchoolPay: false,
      forRemote: false
    },
    {
      type: SERVER.BooksDBConstants.IT_MEMBERSHIP,
      account: true,
      amount: true,
      baseItem: false,
      pos: true,
      subItems: true,
      deposit: true,
      forSchoolPay: true,
      forRemote: true
    },
    {
      type: SERVER.BooksDBConstants.IT_PERMITS,
      account: true,
      amount: true,
      baseItem: false,
      pos: false,
      subItems: false,
      deposit: true,
      forSchoolPay: false,
      forRemote: false
    },
    {
      type: SERVER.BooksDBConstants.IT_PRODUCT,
      account: true,
      amount: true,
      baseItem: false,
      pos: true,
      subItems: true,
      deposit: true,
      forSchoolPay: true,
      forRemote: true
    },
    {
      type: SERVER.BooksDBConstants.IT_RECORDS,
      account: false,
      amount: false,
      baseItem: true,
      pos: false,
      subItems: false,
      deposit: false,
      forSchoolPay: false,
      forRemote: false
    },
    {
      type: SERVER.BooksDBConstants.IT_TEXTBOOKS,
      account: false,
      amount: true,
      baseItem: true,
      pos: false,
      subItems: false,
      deposit: false,
      forSchoolPay: false,
      forRemote: false
    }
  ];

  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Instance reference.  */ var $ctrl = this;

  $ctrl.itemList = ItemAdminService.getItems();

  var $global = {};
  $ctrl.activeTab = 0;
  var mAccountList;
  var mDisbursementDialogActive = false;
  var mItemType;
  var mMembershipTag;

  /*---------------------*/
  /* User feature flags. */
  /*---------------------*/
  /** Remote flag      */
  var mRemote;

  /*------------------*/
  /* Server Constants */
  /*------------------*/
  /** Allow discounts to be modified. */
  const ALLOW_DISCOUNTS = SERVER.BooksWebConstants.allowDiscounts;
  /** Allow Payments option available. */
  const ALLOW_PAYMENTS = SERVER.BooksWebConstants.allowPayments;

  /*------------------------------------*/
  /* Tab Objects - Default the onSelect */
  /* property to use the empy function  */
  /*------------------------------------*/
  $ctrl.tabBasicInfo = {
    header: 'Basic Info',
    templateUrl: AddChangeItemConfig.templateIDs.BasicInfoTab,
    onSelect: angular.noop
  };
  $ctrl.tabSalesInfo = {
    header: 'Sales Info',
    templateUrl: AddChangeItemConfig.templateIDs.SalesInfoTab,
    onSelect: angular.noop
  };
  $ctrl.tabSubItems = {
    header: 'Sub Items',
    templateUrl: AddChangeItemConfig.templateIDs.SubItemsTab,
    onSelect: angular.noop
  };
  $ctrl.tabOptions = {
    header: 'Options',
    templateUrl: AddChangeItemConfig.templateIDs.OptionsTab,
    onSelect: angular.noop
  };
  $ctrl.tabInventory = {
    header: 'Inventory',
    templateUrl: AddChangeItemConfig.templateIDs.InventoryTab,
    onSelect: angular.noop
  };
  $ctrl.tabDiscounts = {
    header: 'Discounts',
    templateUrl: AddChangeItemConfig.templateIDs.DiscountsTab,
    onSelect: angular.noop
  };
  $ctrl.tabTags = {
    header: 'Indicator Tags',
    templateUrl: AddChangeItemConfig.templateIDs.TagsTab,
    onSelect: angular.noop
  };

  /*-------*/
  /* Flags */
  /*-------*/
  $ctrl.uiFlags = Object.defineProperties(
    {},
    {
      forTrust: {
        get: function () {
          return $global.salesAcctType == AccountType.TRUST;
        }
      },
      isBaseItem: {
        get: function () {
          return mItemType.baseItem;
        }
      },
      isDonation: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_DONATION;
        }
      },
      isEquipText: {
        get: function () {
          return (
            mItemType.type == SERVER.BooksDBConstants.IT_EQUIPMENT ||
            $ctrl.uiFlags.isTextbook
          );
        }
      },
      isFee: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_FEES;
        }
      },
      isFine: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_FINES;
        }
      },
      isInvProduct: {
        get: function () {
          return $ctrl.item.defaultProduct && $ctrl.item.defaultProduct.active;
        }
      },
      isGroup: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_GROUP;
        }
      },
      isMembership: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_MEMBERSHIP;
        }
      },
      isPOS: {
        get: function () {
          return mItemType.pos;
        }
      },
      isProduct: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_PRODUCT;
        }
      },
      isRecord: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_RECORD;
        }
      },
      isTextbook: {
        get: function () {
          return mItemType.type == SERVER.BooksDBConstants.IT_TEXTBOOKS;
        }
      },
      requireAmount: {
        get: function () {
          return $ctrl.uiFlags.showAmount && !$ctrl.uiFlags.isFine;
        }
      },
      showAccount: {
        get: function () {
          return mItemType.account;
        }
      },
      showAmount: {
        get: function () {
          return mItemType.amount;
        }
      },
      showDiscounts: {
        get: function () {
          return ALLOW_DISCOUNTS && (this.isProduct || this.isGroup);
        }
      },
      showEffective: {
        get: function () {
          return $ctrl.isSubItemType(mItemType.type);
        }
      },
      showAllowPayments: {
        get: function () {
          return $ctrl.isSubItemType(mItemType.type) && ALLOW_PAYMENTS;
        }
      }
    }
  );

  /*------------*/
  /* Properties */
  /*------------*/
  Object.defineProperties($ctrl, {
    commitLabel: {
      get: function () {
        return (
          ($ctrl.isNewItem ? 'Add New ' : 'Change ') +
          getTypeName(mItemType.type)
        );
      }
    },

    dialogTitle: {
      get: function () {
        var typeName = getTypeName(mItemType.type);
        return $ctrl.isNewItem
          ? 'Add New ' + typeName
          : 'Change ' +
              typeName +
              ($ctrl.item ? ' - ' + ($ctrl.item.shortDescription || '') : '');
      }
    },

    isNewItem: {
      get: function () {
        return Tools.isNaNZero($ctrl.item.id);
      }
    },

    /*-------------------------------------------*/
    /* salesAcctID - Set the account information */
    /* for the first item distribution.          */
    /*-------------------------------------------*/
    salesAcctID: {
      get: function () {
        return $ctrl._salesAcctID;
      },
      set: function (id) {
        $ctrl._salesAcctID = id;

        if (!id) return;
        var selectedAcct = $ctrl.salesAccountList
          .filter(function (acct) {
            return id == acct.accountID;
          })
          .shift() || { accountID: 0, type: 0 };
        var salesAcctItem =
          $ctrl.item.salesAcctList[0] || new BooksData.ItemSalesAcct();
        $ctrl.item.salesAcctList[0] = Object.assign(salesAcctItem, {
          accountID: selectedAcct.accountID,
          percentage: 1,
          amount: 0
        });
        $global.salesAcctType = selectedAcct.type;
      }
    },

    /*---------------------------------------------------------------*/
    /* singleSalesAccount - returns `true` if less than two sales    */
    /* account distributions. Toggle display of account information. */
    /* Multiple distributions hide single account input.             */
    /*---------------------------------------------------------------*/
    singleSalesAccount: {
      get: function () {
        return $ctrl.item.salesAcctList.length <= 1;
      }
    }
  });

  /***************************************************************************
   * $onInit */
  $ctrl.$onInit = function () {
    $ctrl.vendorList = $ctrl.modalInfo.vendorList;
    $ctrl.classList = $ctrl.modalInfo.classList;
    mAccountList = $ctrl.modalInfo.accounts;
    mItemType = TYPES.find(function (type) {
      return type.type == $ctrl.item.type;
    });
    mMembershipTag = $ctrl.tagLists.membershipTags.concat().shift();
    mRemote = $ctrl.modalInfo.revtrak;

    $ctrl.adjustmentAccountList = mAccountList.filter(function (acct) {
      return acct.active && RESTRICTED_ADJ_ACCT_TYPES.indexOf(acct.type) == -1;
    });
    $ctrl.COGSAccountList = mAccountList.filter(function (acct) {
      return acct.active && RESTRICTED_COGS_ACCT_TYPES.indexOf(acct.type) == -1;
    });
    $ctrl.depositAccountList = mAccountList.filter(function (acct) {
      return acct.active && acct.type == AccountType.BANK;
    });
    $ctrl.salesAccountList = mAccountList.filter(function (acct) {
      return acct.active && RESTRICTED_ACCT_TYPES.indexOf(acct.type) == -1;
    });

    $global.salesAcctType = '';

    init();
  };

  /***************************************************************************
   * chkIdentify */
  /**
   * Getter/Setter for the Identify checkbox in the basicInfo & salesInfo
   * template. Returns the inverse of the allowAnonymousSale or sets the inverse
   * of the value to allowAnonymousSale.
   * The activeStudentsOnly flag is an ASB only feature and only ASB items can
   * require a student be identified to purchase the item. If we don't require
   * a student be identified, it wouldn't make sense to make sure the student
   * is active. This is why we set the activeStudentsOnly to false if the
   * allowAnonymousSale is set to true.
   ***************************************************************************/
  $ctrl.chkIdentify = function (value) {
    if (!$ctrl.item) return;

    if (!arguments.length) return !$ctrl.item.allowAnonymousSale;

    $ctrl.item.allowAnonymousSale = !value;
    if ($ctrl.item.allowAnonymousSale) {
      $ctrl.item.activeStudentsOnly = false;
    }
  };

  /***************************************************************************
   * commit */
  /**
   * Perform the save and close the dialog with the updated item. This will use
   * the $resolve method from the AddChangeItemDialog modal service.
   ***************************************************************************/
  $ctrl.commit = function () {
    $ctrl.saveItem().then(function () {
      $ctrl.resolve({ $item: $ctrl.item });
    });
  };

  /*****************************************************************************
   * getTypeName */
  /**
   * Returns the matching type name for a given type id.
   *
   * @param   type    Item Type
   * @return  String  Corresponding name, empty string if none.
   *****************************************************************************/
  function getTypeName(type) {
    var name = '';
    switch (type) {
      case SERVER.BooksDBConstants.IT_EQUIPMENT:
        name = 'Equipment';
        break;
      case SERVER.BooksDBConstants.IT_TEXTBOOKS:
        name = 'Textbook';
        break;
      case SERVER.BooksDBConstants.IT_RECORDS:
        name = 'Record';
        break;
      case SERVER.BooksDBConstants.IT_FEES:
        name = 'Fee';
        break;
      case SERVER.BooksDBConstants.IT_MEMBERSHIP:
        name = 'Membership';
        break;
      case SERVER.BooksDBConstants.IT_PERMITS:
        name = 'Permit';
        break;
      case SERVER.BooksDBConstants.IT_FINES:
        name = 'Fine';
        break;
      case SERVER.BooksDBConstants.IT_PRODUCT:
        name = 'Product';
        break;
      case SERVER.BooksDBConstants.IT_DONATION:
        name = 'Donation';
        break;
      case SERVER.BooksDBConstants.IT_GROUP:
        name = 'Kit';
        break;
    }
    return name;
  }

  /***************************************************************************
   * handleDisbursements */
  /**
   * Event handling for the managing of the ManageDistributionsDialog.
   ***************************************************************************/
  $ctrl.handleDisbursements = function () {
    /*------------------------------*/
    /* Dialog already active, exit. */
    /*------------------------------*/
    if (mDisbursementDialogActive) return;

    /*-----------------------------------------------*/
    /* Set properties that will be passed to dialog. */
    /*-----------------------------------------------*/
    var acctList = ($ctrl.salesAccountList || []).filter(function (ai) {
      return !($ctrl.uiFlags.isInvProduct && ai.type == AccountType.TRUST);
    });
    var itemAmount = $ctrl.item.amount;
    var distributions = $ctrl.item.salesAcctList.map(function (a) {
      return new BooksData.ItemSalesAcct(a);
    });

    /*----------------------------------------------*/
    /* Call the dialog to add/modify distributions. */
    /*----------------------------------------------*/
    mDisbursementDialogActive = true;
    ManageDistributionsDialog.show(
      acctList,
      !$ctrl.uiFlags.isDonation,
      itemAmount,
      distributions
    )
      .then(function (distributions) {
        $ctrl.item.salesAcctList = distributions;
        setSalesAccount();
      })
      ['finally'](function () {
        mDisbursementDialogActive = false;
      });
  };

  /*****************************************************************************
   * init */
  /**
   * Set up the dialog title, labels, visible tabs and components. Called to
   * setup the dialog.
   *****************************************************************************/
  function init() {
    BusyScreen.setBusy(true);

    /*-----------------------------------------------------------*/
    /* This is for a new item, set up new instances for the type */
    /*-----------------------------------------------------------*/
    if ($ctrl.isNewItem) {
      /*-----------------------------------------------------------------*/
      /* If type isn't a base item type, then this is for a new product. */
      /*-----------------------------------------------------------------*/
      if (!$ctrl.uiFlags.isBaseItem) {
        /*------------------------------------------------------*/
        /* If this is a membership item, add the membership tag */
        /*------------------------------------------------------*/
        if ($ctrl.uiFlags.isMembership) {
          $ctrl.item.allowAnonymousSale = false;

          $ctrl.item.membershipTags = [mMembershipTag];

          /**
           * This check was added to cover for insufficient checking before this. Basically the client can attempt to create
           * memberships before they even have a tag setup in the DB, before this would always just error. Now if they are missing
           * a membership tag it tells them so and closes the modal so they can not run into the error.
           */
          if (!mMembershipTag) {
            Alert.error('Membership tag is missing. Please contact Support.');
            $ctrl.cancel();
          }
        }
        initProduct();
      }
    } else {
      /*---------------------------*/
      /* Otherwise this is an edit */
      /*---------------------------*/
      /*---------------------------------------------------------------------*/
      /* Ensure inactive accounts are available to the appropriate lists     */
      /*                                                                     */
      /* TODO: If this is not done here, initProduct() (below) will wipe out */
      /* TODO: the single sales account if it is inactive.                   */
      /*---------------------------------------------------------------------*/
      if ($ctrl.uiFlags.showAccount) {
        /*-------------------------------*/
        /* Update the sales account list */
        /*-------------------------------*/
        ($ctrl.item.salesAcctList || [])
          .filter(function (isa) {
            return !$ctrl.salesAccountList.some(function (sa) {
              return sa.accountID == isa.accountID;
            });
          })
          .forEach(function (isa) {
            var found = mAccountList
              .filter(function (a) {
                return a.accountID == isa.accountID;
              })
              .shift();
            if (found) $ctrl.salesAccountList.push(found);
          });
      }

      /*--------------------------------------------------*/
      /* Set product if the type is not a base item type. */
      /*--------------------------------------------------*/
      if (!$ctrl.uiFlags.isBaseItem) initProduct();

      /*------------------------------*/
      /* Update the COGS account list */
      /*------------------------------*/
      if (
        $ctrl.uiFlags.isProduct &&
        $ctrl.item.cogsAcctID &&
        !$ctrl.COGSAccountList.some(function (sa) {
          return sa.accountID == $ctrl.item.cogsAcctID;
        })
      )
        $ctrl.COGSAccountList.push(
          mAccountList
            .filter(function (a) {
              return a.accountID == $ctrl.item.cogsAcctID;
            })
            .shift()
        );

      /*---------------------------------*/
      /* Update the Deposit account list */
      /*---------------------------------*/
      if (
        $ctrl.showDeposit() &&
        $ctrl.item.depositAcctID &&
        !$ctrl.depositAccountList.some(function (sa) {
          return sa.accountID == $ctrl.item.depositAcctID;
        })
      )
        $ctrl.depositAccountList.push(
          mAccountList
            .filter(function (a) {
              return a.accountID == $ctrl.item.depositAcctID;
            })
            .shift()
        );
    }

    /*--------------------------------------------------*/
    /* Set up the tabs that are available for this item */
    /*--------------------------------------------------*/
    $ctrl.tabs = [$ctrl.tabBasicInfo];
    if ($ctrl.uiFlags.isProduct) $ctrl.tabs.push($ctrl.tabSalesInfo);
    if ($ctrl.uiFlags.isGroup) $ctrl.tabs.push($ctrl.tabSubItems);
    if ($ctrl.uiFlags.isDonation || $ctrl.uiFlags.isProduct)
      $ctrl.tabs.push($ctrl.tabOptions);
    if ($ctrl.uiFlags.isProduct) $ctrl.tabs.push($ctrl.tabInventory);
    if ($ctrl.uiFlags.showDiscounts) $ctrl.tabs.push($ctrl.tabDiscounts);
    if ($ctrl.uiFlags.isProduct || $ctrl.uiFlags.isGroup)
      $ctrl.tabs.push($ctrl.tabTags);

    /*--------------------------------------------*/
    /* If there is only one tab, hide the tab bar */
    /*--------------------------------------------*/
    if ($ctrl.tabs.length == 1)
      angular
        .element(document.querySelector('#addChangeItemDialogTabs'))
        .addClass('hideBar');

    BusyScreen.setBusy(false);
  }

  /***************************************************************************
   * initProduct */
  /**
   * Initialize the dialog for product management.
   ***************************************************************************/
  function initProduct() {
    setSalesAccount();

    /*-----------------------------------------------------------------*/
    /* Adds an accessor to the item for the default inventory product. */
    /* The inventory product is added if not found for product items   */
    /*-----------------------------------------------------------------*/
    Object.defineProperty($ctrl.item, 'defaultProduct', {
      get: function () {
        /**
         * Grab the default product. By design the default product is the
         * first product in the list of inventory products and it will
         * always be an empty options list.
         */
        var product = (this.inventoryProducts || [])
          .filter(function (ip) {
            return !ip.options.length;
          })
          .shift();

        /*------------------------------------------------------------------*/
        /* If a default product wasn't found, create a new one for the      */
        /* Product type only, and add it to the list of inventory products. */
        /*------------------------------------------------------------------*/
        if (!product && $ctrl.uiFlags.isProduct) {
          if (!this.inventoryProducts) this.inventoryProducts = [];

          product = new InventoryProduct();
          product.active = false;
          this.inventoryProducts.push(product);
        }

        return product;
      }
    });
  }

  /***************************************************************************
   * isSubItemType */
  /**
   * Returns an indication if the passed item type can be a subitem in a kit.
   *
   * @param    type     Item Type
   * @return   Boolean  True=item type can be used.
   ***************************************************************************/
  $ctrl.isSubItemType = function (type) {
    return (
      TYPES.filter(function (t) {
        return t.subItems;
      })
        .map(function (sub) {
          return sub.type;
        })
        .indexOf(type) > -1
    );
  };

  /***************************************************************************
   * saveItem */
  /**
   * Handles the saving of the item. Returns a promise that resolves on a
   * successful save.
   *
   * @return promise Resolves on save success
   ***************************************************************************/
  $ctrl.saveItem = function () {
    /*---------------------------------*/
    /* Clear alerts & reset validators */
    /*---------------------------------*/
    Alert.clear();
    $ctrl.tabs.forEach(function (tab) {
      if (tab.validators) tab.validators.reset();
    });

    /*---------------------------------------------------*/
    /* Validate the tabs, making the invalid tab active. */
    /*---------------------------------------------------*/
    for (var i = 0; i < $ctrl.tabs.length; i++)
      if (
        $ctrl.tabs[i].validators &&
        !$ctrl.tabs[i].validators.validate(true)
      ) {
        $ctrl.activeTab = i;
        return $q.reject();
      }

    /*---------------------------------------*/
    /* Fines without an amount should be $0. */
    /*---------------------------------------*/
    if ($ctrl.uiFlags.isFine && isNaN($ctrl.item.amount)) $ctrl.item.amount = 0;

    /*------------------------------------------------------------*/
    /* For product items, ensure the COGS account ID is not null. */
    /*------------------------------------------------------------*/
    if ($ctrl.uiFlags.isProduct)
      if (!$ctrl.item.cogsAcctID) $ctrl.item.cogsAcctID = 0;

    /*-------------------------------------*/
    /* Call service to add/change the item */
    /*-------------------------------------*/
    BusyScreen.setBusy(true);
    return ItemAdminService.saveItem($ctrl.item).then(
      function (id) {
        BusyScreen.setBusy(false);

        /*------------------------------*/
        /* Set the id property on item. */
        /*------------------------------*/
        $ctrl.item.id = id;
        Alert.success('Item saved successfully', 'Item saved');
      },

      /*-------------------------------------------*/
      /* Handle error that occurred during saving. */
      /*-------------------------------------------*/
      function (resp) {
        BusyScreen.setBusy(false);
        Alert.error(resp.parsedResponse.message());
        return $q.reject();
      }
    );
  };

  /***************************************************************************
   * setSalesAccount */
  /**
   * Enables/disables the Sales Account selector based on the number of
   * distributions on the product.
   ***************************************************************************/
  function setSalesAccount() {
    /*-----------------------------------------------------*/
    /* If there is only one account, set the sales account */
    /* ID on the item for use with the Account Selector.   */
    /* The accessor will set the `salesAcctType`.          */
    /*-----------------------------------------------------*/
    if ($ctrl.item.salesAcctList.length == 1)
      $ctrl.salesAcctID = $ctrl.item.salesAcctList[0].accountID;
  }

  /*****************************************************************************
   * showClass */
  /**
   * Return an indication if the class selection is to be used.
   *****************************************************************************/
  $ctrl.showClass = function () {
    return (
      SERVER.BooksWebConstants.withClass &&
      ($ctrl.uiFlags.isDonation ||
        $ctrl.uiFlags.isGroup ||
        $ctrl.uiFlags.isProduct)
    );
  };

  /*****************************************************************************
   * showDeposit */
  /**
   * When the user has SchoolPay and RevTrak, the deposit account input can be
   * displayed. Used to show/hide the Deposit Account input.
   *
   * TODO: Once we decide to remove revtrak this can be taken out since we are no longer using schoolpay as well.
   *
   * @return   Boolean  True to display deposit, false otherwise.
   *****************************************************************************/
  $ctrl.showDeposit = function () {
    for (var i = 0; i < TYPES.length; i++)
      if (TYPES[i].deposit && mItemType.type == TYPES[i].type)
        return mRemote && TYPES[i].forRemote;
    return false;
  };
}
